import { Package, RefreshCw, Plus, Edit3, Trash2 } from 'lucide-react';
import { Product } from '../../../api/products';
import { cn } from '../../../utils/responsive';
import { Surface } from '../../../components/ui/Surface';
import { EmptyState } from '../../../components/ui/EmptyState';
import { resolveProductImage } from '../constants';

interface ProductTableProps {
    products: Product[];
    isLoading: boolean;
    isMobileOrTablet: boolean;
    searchTerm: string;
    onEdit: (product: Product) => void;
    onDelete: (product: Product) => void;
    onToggleStatus: (params: { id: string; isActive: boolean }) => void;
    onOpenLightbox: (images: Product['images'], index: number) => void;
    pagination: any;
    page: number;
    onPageChange: (page: number) => void;
    onCreateClick: () => void;
    isTogglePending: boolean;
}

export function ProductTable({
    products,
    isLoading,
    isMobileOrTablet,
    searchTerm,
    onEdit,
    onDelete,
    onToggleStatus,
    onOpenLightbox,
    pagination,
    page,
    onPageChange,
    onCreateClick,
    isTogglePending,
}: ProductTableProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-gray-500 dark:text-gray-400">
                <RefreshCw className="h-4 w-4 animate-spin" />
                กำลังโหลดสินค้า...
            </div>
        );
    }

    if (products.length === 0) {
        return (
            <EmptyState
                className="m-6"
                icon={<Package className="h-5 w-5" />}
                title="ยังไม่มีสินค้าที่ตรงกับเงื่อนไข"
                description={searchTerm ? 'ลองปรับคำค้นหรือสร้างสินค้าใหม่' : 'กดเพิ่มสินค้าเพื่อเริ่มต้นรายการสินค้า'}
                actions={(
                    <button
                        onClick={onCreateClick}
                        className="inline-flex items-center gap-2 rounded-lg border border-transparent bg-yellow-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-yellow-600"
                    >
                        <Plus className="h-4 w-4" />
                        เพิ่มสินค้าใหม่
                    </button>
                )}
            />
        );
    }

    const renderTags = (tags: string[] = []) => {
        if (tags.length === 0) return <span className="text-xs text-gray-400 dark:text-gray-500">-</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {tags.map((tag, idx) => (
                    <span
                        key={idx}
                        className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full"
                        style={{
                            backgroundColor: tag.includes('ลด') || tag.includes('ดีล') ? '#FEE2E2' :
                                tag.includes('ใหม่') ? '#D1FAE5' :
                                    tag.includes('ขาย') ? '#FEF3C7' :
                                        tag.includes('Premium') || tag.includes('Exclusive') ? '#E0E7FF' : '#F3F4F6',
                            color: tag.includes('ลด') || tag.includes('ดีล') ? '#991B1B' :
                                tag.includes('ใหม่') ? '#065F46' :
                                    tag.includes('ขาย') ? '#92400E' :
                                        tag.includes('Premium') || tag.includes('Exclusive') ? '#3730A3' : '#374151'
                        }}
                    >
                        {tag}
                    </span>
                ))}
            </div>
        );
    };

    return (
        <Surface padding="none" className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-700">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">รายการสินค้า</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {pagination?.total.toLocaleString('th-TH')} รายการทั้งหมด
                    </p>
                </div>
                {searchTerm && (
                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                        ค้นหา: {searchTerm}
                    </span>
                )}
            </div>

            {isMobileOrTablet ? (
                <div className="p-4 space-y-3">
                    {products.map((product) => (
                        <div key={product.id} className={cn(
                            "bg-white dark:bg-gray-800 rounded-lg border border-stone-200 p-4 hover:border-yellow-400 hover:shadow-accent transition-all duration-200",
                            !product.isActive && "opacity-60 grayscale-[0.3] bg-stone-50/50 dark:bg-gray-800/50"
                        )}>
                            <div className="flex gap-3 mb-3">
                                <div className="flex-shrink-0 cursor-pointer" onClick={() => onOpenLightbox(product.images, 0)}>
                                    {product.images[0] ? (
                                        <img src={resolveProductImage(product.images[0], 'thumbnail')} alt={product.name} className="h-20 w-20 rounded-lg object-cover border border-stone-200 hover:opacity-80 transition-opacity" />
                                    ) : (
                                        <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-stone-200 text-stone-400 dark:bg-gray-700 dark:text-gray-500">
                                            <Package className="h-8 w-8" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider', product.warehouse === 'HYB' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400')}>
                                            {product.warehouse || 'HYB'}
                                        </span>
                                        <p className="text-sm font-medium text-stone-900 dark:text-gray-100 line-clamp-2">{product.name} {product.nameTh && `(${product.nameTh})`}</p>
                                    </div>
                                    <div className="text-xl font-mono font-bold text-emerald-600 mt-1">฿{product.price.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                    <div className="text-xs text-stone-500 dark:text-gray-400 mt-1">{product.variants?.length || 0} ตัวเลือก</div>
                                </div>
                            </div>
                            {renderTags((product as any).tags)}
                            <div className="flex items-center gap-2 pt-3 border-t border-stone-200 mt-3">
                                <button
                                    onClick={() => onToggleStatus({ id: product.id, isActive: !product.isActive })}
                                    disabled={isTogglePending}
                                    className={cn('relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-200 disabled:opacity-50', product.isActive ? 'bg-emerald-500' : 'bg-stone-300 dark:bg-gray-600')}
                                >
                                    <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white transition-transform', product.isActive ? 'translate-x-6' : 'translate-x-1')} />
                                </button>
                                {!product.isActive && (
                                    <span className="text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30">
                                        ไม่แสดงบน App
                                    </span>
                                )}
                                <div className="flex-1" />
                                <button onClick={() => onEdit(product)} className="inline-flex items-center gap-1.5 rounded-lg bg-yellow-100 px-3 py-2 text-xs font-medium text-yellow-700 transition-all duration-200 hover:bg-yellow-200 active:scale-95 dark:bg-yellow-900/40 dark:text-yellow-300 dark:hover:bg-yellow-900/60">
                                    <Edit3 className="h-3.5 w-3.5" /> แก้ไข
                                </button>
                                <button onClick={() => onDelete(product)} className="inline-flex items-center rounded-lg bg-red-100 px-3 py-2 text-xs font-medium text-red-600 transition-all duration-200 hover:bg-red-200 active:scale-95 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-stone-700 dark:text-gray-200">
                        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            <tr>
                                <th className="px-6 py-3 text-left">รูป</th>
                                <th className="px-6 py-3 text-left">ชื่อสินค้า</th>
                                <th className="px-6 py-3 text-left">ราคา</th>
                                <th className="px-6 py-3 text-left">ตัวเลือก</th>
                                <th className="px-6 py-3 text-left">แท็ก</th>
                                <th className="px-6 py-3 text-left">สถานะ</th>
                                <th className="px-6 py-3 text-right">การจัดการ</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {products.map((product) => (
                                <tr key={product.id} className={cn(
                                    "hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-opacity duration-200",
                                    !product.isActive && "opacity-60 grayscale-[0.3] bg-gray-50/30 dark:bg-gray-800/30"
                                )}>
                                    <td className="px-6 py-4">
                                        {product.images[0] ? (
                                            <img src={resolveProductImage(product.images[0], 'thumbnail')} alt={product.name} className="h-12 w-12 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity" onClick={() => onOpenLightbox(product.images, 0)} title="คลิกเพื่อดูรูปภาพขนาดเต็ม" />
                                        ) : (
                                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500">
                                                <Package className="h-5 w-5" />
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap', product.warehouse === 'HYB' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400')}>
                                                {product.warehouse || 'HYB'}
                                            </span>
                                            <div className="font-medium text-gray-900 dark:text-white">{product.nameTh || product.name}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">฿{product.price.toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400">{product.variants?.length || 0} ตัวเลือก</td>
                                    <td className="px-6 py-4">{renderTags((product as any).tags)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => onToggleStatus({ id: product.id, isActive: !product.isActive })}
                                            disabled={isTogglePending}
                                            className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50', product.isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600')}
                                        >
                                            <span className={cn('inline-block h-4 w-4 transform rounded-full bg-white transition-transform', product.isActive ? 'translate-x-6' : 'translate-x-1')} />
                                        </button>
                                        {!product.isActive && (
                                            <div className="mt-1">
                                                <span className="text-[10px] font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30 whitespace-nowrap">
                                                    ไม่แสดงบน App
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="flex items-center justify-end gap-2 text-sm">
                                            <button onClick={() => onEdit(product)} className="inline-flex items-center rounded-lg bg-yellow-100 px-2 py-1 text-yellow-700 transition hover:bg-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:hover:bg-yellow-900/60">
                                                <Edit3 className="h-4 w-4" />
                                            </button>
                                            <button onClick={() => onDelete(product)} className="inline-flex items-center rounded-lg bg-red-100 px-2 py-1 text-red-600 transition hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination && pagination.pages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-stone-100 bg-stone-50 px-4 lg:px-6 py-3 lg:py-4 text-xs lg:text-sm text-stone-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    <div>แสดง {products.length} จาก {pagination.total.toLocaleString('th-TH')} รายการ</div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1} className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs lg:text-sm transition-all duration-200 enabled:hover:bg-stone-100 enabled:hover:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:enabled:hover:bg-gray-700">ก่อนหน้า</button>
                        <span className="px-2 lg:px-3 py-1 font-mono">{page} / {pagination.pages}</span>
                        <button onClick={() => onPageChange(Math.min(pagination.pages, page + 1))} disabled={page === pagination.pages} className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs lg:text-sm transition-all duration-200 enabled:hover:bg-stone-100 enabled:hover:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:enabled:hover:bg-gray-700">ถัดไป</button>
                    </div>
                </div>
            )}
        </Surface>
    );
}
